import React from "react";

/**
 * A custom React hook to measure the width of certain table cell elements.
 * Returns refs to relevant HTMLTableCellElement instances and their respective widths.
 * Measured widths are updated in response to changes in the current element offset widths.
 *
 * @returns An object containing refs to relevant HTMLTableCellElement instances and their respective widths.
 */
const useCopyElementWidth = () => {
  const typeRef = React.useRef<HTMLTableCellElement>(null);
  const dateRef = React.useRef<HTMLTableCellElement>(null);
  const timeRef = React.useRef<HTMLTableCellElement>(null);
  const currencyRef = React.useRef<HTMLTableCellElement>(null);
  const amountRef = React.useRef<HTMLTableCellElement>(null);
  const statusRef = React.useRef<HTMLTableCellElement>(null);

  const [width, setWidth] = React.useState<Record<string, number>>({
    type: 0,
    date: 0,
    time: 0,
    currency: 0,
    amount: 0,
    status: 0,
  });

  React.useEffect(() => {
    setWidth((prev) => ({
      ...prev,
      type: typeRef.current?.offsetWidth ?? prev.type,
      date: dateRef.current?.offsetWidth ?? prev.date,
      time: timeRef.current?.offsetWidth ?? prev.time,
      currency: currencyRef.current?.offsetWidth ?? prev.currency,
      amount: amountRef.current?.offsetWidth ?? prev.amount,
      status: statusRef.current?.offsetWidth ?? prev.status,
    }));
  }, [
    typeRef?.current?.offsetWidth,
    dateRef?.current?.offsetWidth,
    timeRef?.current?.offsetWidth,
    currencyRef?.current?.offsetWidth,
    amountRef?.current?.offsetWidth,
    statusRef?.current?.offsetWidth,
  ]);

  return {
    typeRef,
    dateRef,
    timeRef,
    currencyRef,
    amountRef,
    statusRef,
    width,
  };
};

export default useCopyElementWidth;
